import React, { isValidElement, ReactNode, useMemo, useState } from "react";
import {
  TableCell,
  TableHead,
  TableBody,
  TablePagination,
  TableProps,
  TableRow,
  Paper,
} from "@mui/material";
import { t } from "i18next";
import { isEmpty } from "lodash";
import DOMPurify from "dompurify";

import { StyledTableContainer, StyledMuiTable } from "./Table.styled";
import {
  OpenRow,
  PaginationProps,
  ParentSelection,
  TableColumn,
} from "./Table.types";

import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  DEFAULT_TOTAL_ITEMS,
} from "@constants";
import { Checkbox, Skeleton } from "@components/base";
interface RowWithId {
  id: string | number;
}

/*
 * A generic table component with support for pagination, row selection, and drag-and-drop.
 * T - Type of the data items
 */
interface Table<T extends RowWithId> {
  columns: TableColumn<T>[];
  data: T[];
  isLoading?: boolean;

  /* Additional props for the MUI Table component */
  tableProps?: TableProps;

  /* Placeholder content when there is no data */
  dataPlaceholder?: string | ReactNode;

  /* Callback when a row is clicked */
  onRowClick?: (record: T) => void;

  /* Pagination props */
  pagination?: PaginationProps;

  /* Drag-and-drop related props */
  isDropAllowed?: (dragged: T, droppedOn: T) => boolean;
  onDropRow?: (dragged: T, droppedOn: T) => void;

  /*
   * If true, enables row selection with checkboxes.
   * selectedRowIds and onSelectionChange props will be used to control selection state.
   */
  enableRowSelection?: boolean;
  selectedRowIds?: string[] | ParentSelection[];
  onSelectionChange?: (selectedIds: string[]) => void;

  /*
   * Rows that are expanded to show additional content.
   * onRowClick or TableColumn.onCellClick can be used as trigger
   */
  openRows?: OpenRow[];
}

const Table = <T extends RowWithId>({
  columns,
  data,
  dataPlaceholder,
  onRowClick,
  pagination,
  tableProps,
  onDropRow,
  isDropAllowed,
  isLoading,
  selectedRowIds = [],
  enableRowSelection = false,
  onSelectionChange,
  openRows,
}: Table<T>): JSX.Element => {
  const controlledSelectedIndexes = useMemo(() => {
    if (!selectedRowIds) return null;

    const idToIndex = new Map(data.map((r, i) => [String(r.id), i]));

    return selectedRowIds
      .map((id) =>
        typeof id === "object"
          ? idToIndex.get(String(id.id))
          : idToIndex.get(String(id)),
      )
      .filter((idx) => idx !== undefined) as number[];
  }, [selectedRowIds, data]);

  const [internalSelectedRows, setInternalSelectedRows] = useState<number[]>([]);
  const selectedRows = controlledSelectedIndexes ?? internalSelectedRows;

  const updateSelection = (newIndexes: number[]): void => {
    if (controlledSelectedIndexes !== null) {
      onSelectionChange?.(newIndexes.map((idx) => String(data[idx].id)));
    } else {
      setInternalSelectedRows(newIndexes);
      onSelectionChange?.(newIndexes.map((idx) => String(data[idx].id)));
    }
  };

  const [draggedRowIndex, setDraggedRowIndex] = useState<number | null>(null);
  const [dragOverRowIndex, setDragOverRowIndex] = useState<number | null>(null);

  const allIds = useMemo(() => data.map((row, index) => index), [data]);

  const handleSelectAll = (checked: boolean): void => {
    const indices = checked ? data.map((_, i) => i) : [];

    updateSelection(indices);
  };

  const handleSelect = (rowIndex: number): void => {
    const updated = selectedRows.includes(rowIndex)
      ? selectedRows.filter((i) => i !== rowIndex)
      : [...selectedRows, rowIndex];

    updateSelection(updated);
  };

  const handleTableRowClick = (record: T): void => {
    onRowClick?.(record);
  };

  const handlePageSizeChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e): void => {
    const newSize = parseInt(e.target.value, 10);

    pagination?.onPageNumberChange?.(1);
    pagination?.onPageSizeChange?.(newSize);
  };

  const totalColumns = columns.length + (enableRowSelection ? 1 : 0);

  return (
    <StyledTableContainer as={Paper}>
      <StyledMuiTable {...tableProps}>
        <TableHead>
          <TableRow>
            {enableRowSelection && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={!isEmpty(data) && data.length < allIds.length}
                  checked={!isEmpty(allIds) && selectedRows.length === allIds.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell key={`table-head-${column.key}`} {...column.tableCellProps}>
                {column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`skeleton-row-${i}`}>
                  {enableRowSelection && (
                    <TableCell padding="checkbox">
                      <Skeleton variant="rectangular" width={24} height={24} />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell align="center" key={`skeleton-${column.key}`}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : data.map((record, rowIndex) => {
                const isSelected = selectedRows.includes(rowIndex);
                const openRow = openRows?.find(
                  (openRow) => openRow.id === String(record.id),
                );

                let selectionIndeterminate = false;

                if (selectedRowIds) {
                  const selectionEntry = selectedRowIds.find((sel) =>
                    typeof sel === "object"
                      ? String(sel.id) === String(record.id)
                      : false,
                  );

                  if (selectionEntry && typeof selectionEntry === "object") {
                    selectionIndeterminate = selectionEntry.indeterminate;
                  }
                }

                const isDropTarget =
                  typeof isDropAllowed === "function" && draggedRowIndex !== null
                    ? isDropAllowed(data[draggedRowIndex], record)
                    : false;

                return (
                  <>
                    <TableRow
                      key={record.id}
                      hover
                      selected={isSelected}
                      onClick={() => handleTableRowClick(record)}
                      draggable={!!onDropRow && !!isDropAllowed}
                      onDragStart={() => setDraggedRowIndex(rowIndex)}
                      onDragEnd={() => {
                        setDraggedRowIndex(null);
                        setDragOverRowIndex(null);
                      }}
                      onDragOver={(e) => {
                        if (isDropTarget && draggedRowIndex !== rowIndex) {
                          e.preventDefault();
                          setDragOverRowIndex(rowIndex);
                        }
                      }}
                      onDragLeave={() => setDragOverRowIndex(null)}
                      onDrop={() => {
                        setDragOverRowIndex(null);
                        if (
                          typeof onDropRow === "function" &&
                          draggedRowIndex !== null &&
                          draggedRowIndex !== rowIndex &&
                          isDropTarget
                        ) {
                          onDropRow(data[draggedRowIndex], record);
                        }
                      }}
                      style={
                        dragOverRowIndex === rowIndex && isDropTarget
                          ? { background: "#e0f7fa" }
                          : undefined
                      }
                    >
                      {enableRowSelection && (
                        <TableCell
                          padding="checkbox"
                          onClick={(e): void => {
                            e.stopPropagation();
                            handleSelect(rowIndex);
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            indeterminate={selectionIndeterminate}
                          />
                        </TableCell>
                      )}
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={`${column.key}-${record.id}`}
                          {...column.tableCellProps}
                          onClick={
                            column.onCellClick
                              ? (e): void => {
                                  e.stopPropagation();
                                  column.onCellClick?.(record);
                                }
                              : colIndex === columns.length - 1
                                ? (e): void => {
                                    e.stopPropagation();
                                  }
                                : undefined
                          }
                          sx={column.onCellClick ? { cursor: "pointer" } : undefined}
                        >
                          {column.renderCellContent
                            ? column.renderCellContent(record)
                            : column.dataKey
                              ? (() => {
                                  const cellValue = record[column.dataKey];

                                  if (isValidElement(cellValue)) {
                                    return cellValue;
                                  }

                                  if (
                                    column.isHtml &&
                                    typeof cellValue === "string"
                                  ) {
                                    return (
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: DOMPurify.sanitize(cellValue),
                                        }}
                                      />
                                    );
                                  }

                                  if (
                                    typeof cellValue === "object" &&
                                    cellValue !== null
                                  ) {
                                    return JSON.stringify(cellValue);
                                  }

                                  return cellValue != null
                                    ? cellValue.toString()
                                    : null;
                                })()
                              : null}
                        </TableCell>
                      ))}
                    </TableRow>
                    {openRow && (
                      <TableRow>
                        <TableCell
                          colSpan={totalColumns}
                          sx={{
                            p: 0,
                            borderBottom: "none",
                          }}
                        >
                          {openRow.content}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
          {isEmpty(data) && dataPlaceholder && (
            <TableRow>
              <TableCell colSpan={totalColumns} align="center">
                {dataPlaceholder}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </StyledMuiTable>
      {pagination && (
        <TablePagination
          component="div"
          rowsPerPageOptions={
            pagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS
          }
          count={pagination.totalItems ?? DEFAULT_TOTAL_ITEMS}
          page={(pagination.pageNumber ?? DEFAULT_PAGE_NUMBER) - 1}
          onPageChange={(event, newPageIndex) => {
            pagination.onPageNumberChange?.(newPageIndex + 1);
          }}
          rowsPerPage={pagination.pageSize ?? DEFAULT_PAGE_SIZE}
          onRowsPerPageChange={handlePageSizeChange}
          labelRowsPerPage={t("table.pagination.rowsPerPage")}
        />
      )}
    </StyledTableContainer>
  );
};

export default Table;
